import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/common";
import { jobsService } from "@/api/services/jobs.service";
import { checklistTemplatesService } from "@/api/services/checklist-templates.service";
/**
 * Custom hook for creating a Job from a Quote/Estimate
 * Handles API call, loading state, error handling, and success notifications
 * Uses the dedicated createJobFromQuote API endpoint that maps quote fields automatically
 */
export function useCreateJobFromQuote() {
    const { success, error: showError } = useToast();
    const navigate = useNavigate();
    const [isCreating, setIsCreating] = useState(false);
    /**
     * Create a job from a quote using the backend API
     * The backend handles all field mapping and validation
     */
    const createJob = async (quote, options) => {
        try {
            setIsCreating(true);
            // Use the dedicated createJobFromQuote API endpoint
            const response = await jobsService.createJobFromQuote(quote.id);
            const jobId = response.data.id;
            // Insert checklist templates (same behavior as Add Job)
            try {
                const templatesResponse = await checklistTemplatesService.getChecklistTemplates(1, 100);
                const templates = templatesResponse.data.data ?? [];
                if (templates.length > 0) {
                    for (const template of templates) {
                        try {
                            await jobsService.createJobChecklistItem({
                                jobId,
                                description: template.description,
                            });
                        }
                        catch {
                            // Continue with other items; job is already created
                        }
                    }
                }
            }
            catch {
                // Job is already created; checklist insertion is best-effort
            }
            success("Job created successfully!");
            if (options?.navigateToJob) {
                const basePath = window.location.pathname.startsWith("/staff")
                    ? "/staff"
                    : "/admin";
                navigate(`${basePath}/jobs/${jobId}/edit`);
            }
            return response.data;
        }
        catch (err) {
            const errorMessage = err?.response?.data?.message || "Failed to create job";
            showError(errorMessage);
            throw err;
        }
        finally {
            setIsCreating(false);
        }
    };
    /**
     * Navigate to edit an existing job
     */
    const navigateToJob = (jobId) => {
        const basePath = window.location.pathname.startsWith("/staff")
            ? "/staff"
            : "/admin";
        navigate(`${basePath}/jobs/${jobId}/edit`);
    };
    return { createJob, navigateToJob, isCreating };
}
